//core
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Checkbox, FormControlLabel, Grid, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

//styles
import { useStyles } from "./styles";

//selectors
import { 
  selectRestoreStatisticsDatesData, 
  selectRestoreStatisticsDatesError, 
  selectRestoreStatisticsDatesIsFetching,
  selectRestoreStatisticsTaskData,
  selectRestoreStatisticsTaskError,
  selectRestoreStatisticsTaskIsFetching,
} from "../../../../../../store/admin/reports/full_report/selectors";

//helpers
import { format } from "date-fns";
import { isObjectEmpty } from "../../../../../../helpers/isObjectEmpty";

//hooks
import { useFullReport } from "../../../../../../store/admin/reports/full_report/useFullReport";
import { useUI } from "../../../../../../store/common/ui/useUI";

//components
import Button from "../../../../../UI/Buttons/Button";
import { MuiSingleDatePicker } from "../../../../../UI/FormFields/MuiSingleDatePicker";

//validation
import { validationDate } from "../../../../../validation/date.validation";

interface IReturnPlayerModalProps {
  setToggleModal: (state: boolean) => void;
  compositePlayerId: string | null;
  filterType: string | null;
}
export interface IFormValues {
  returnFtd: boolean;
  returnStats: boolean;
  ftdDate: Date | null;
  dateFrom: Date | null;
  dateTo: Date | null;
}

const ReturnPlayerModal: React.FC<IReturnPlayerModalProps> = ({ setToggleModal, compositePlayerId, filterType }) => {
  const { t, i18n } = useTranslation();
  const formRef = useRef<FormikProps<IFormValues>>(null);
  const submitStartedRef = useRef(false);
  const styles = useStyles();

  const { getRestoreStatisticsDates, setRestoreStatisticsTask } = useFullReport();
  const { setGeneralSnackbar } = useUI();

  const restoreDatesData = useSelector(selectRestoreStatisticsDatesData);
  const restoreDatesError = useSelector(selectRestoreStatisticsDatesError);
  const restoreDatesIsFetching = useSelector(selectRestoreStatisticsDatesIsFetching);
  const restoreTaskData = useSelector(selectRestoreStatisticsTaskData);
  const restoreTaskError = useSelector(selectRestoreStatisticsTaskError);
  const restoreTaskIsFetching = useSelector(selectRestoreStatisticsTaskIsFetching);

  const [errorsMessages, setErrorsMessages] = useState({
    required: t("common.validation.required_field"),
    invalidDate: t("common.validation.invalid_date"),
  });

  useEffect(() => {
    setErrorsMessages({
      required: t("common.validation.required_field"),
      invalidDate: t("common.validation.invalid_date"),
    });

    if (formRef.current) {
      if (!isObjectEmpty(formRef.current.errors)) {
        setTimeout(() => {
          formRef.current?.validateForm();
        }, 50);
      }
    }
  }, [i18n.language, t]);

  useEffect(() => {
    if (compositePlayerId) {
      getRestoreStatisticsDates({ compositePlayerId });
    }
  }, [compositePlayerId]);

  useEffect(() => {
    if (restoreDatesError) {
      setGeneralSnackbar({
        open: true,
        type: "error",
        messageKey: "admin.full_report.return_player.unable_to_load_dates",
      });
    }
  }, [restoreDatesError]);

 useEffect(() => {
  if (!submitStartedRef.current) return;
  if (restoreTaskIsFetching) return;

  if (restoreTaskError) {
    const backendErrorKey = restoreTaskError.error;

    setGeneralSnackbar({
      open: true,
      type:
        backendErrorKey ===
        "common.errors.multiData.player.unfilteredPlayerRequiresStatisticsReturn"
          ? "warning"
          : "error",
      messageKey:
        backendErrorKey ===
        "common.errors.multiData.player.unfilteredPlayerRequiresStatisticsReturn"
          ? "admin.full_report.return_player.ftd_already_restored"
          : "admin.full_report.return_player.unable_to_restore_player",
    });

    formRef.current?.setSubmitting(false);
    submitStartedRef.current = false;
    return;
  }

  if (restoreTaskData) {
    setGeneralSnackbar({
      open: true,
      type: "success",
      messageKey: "admin.full_report.return_player.request_processing",
    });

    formRef.current?.setSubmitting(false);
    submitStartedRef.current = false;
    setToggleModal(false);
  }
}, [restoreTaskIsFetching, restoreTaskError, restoreTaskData]);

  const initialValues: IFormValues = useMemo(() => ({
    returnFtd: true,
    returnStats: false,
    ftdDate: restoreDatesData?.ftdDate ? new Date(restoreDatesData.ftdDate) : null,
    dateFrom: restoreDatesData?.dateFrom ? new Date(restoreDatesData.dateFrom) : null,
    dateTo: restoreDatesData?.dateTo ? new Date(restoreDatesData.dateTo) : null,
  }), [restoreDatesData]);

  const handleSubmit = (values: IFormValues) => {
    if (!compositePlayerId) return;

    submitStartedRef.current = true;

    const toApiDate = (value: Date | null) =>
      value ? format(value, "yyyy-MM-dd") : null;

    const payload = {
      compositePlayerId,
      ftdRestoreDate: toApiDate(values.ftdDate),
      filterType,
      shouldReturnOtherStatistics: values.returnStats,
      otherStatisticsReturnFromDate: values.returnStats
        ? toApiDate(values.dateFrom)
        : null,
      otherStatisticsReturnToDate: values.returnStats
        ? toApiDate(values.dateTo)
        : null,
    };

    setRestoreStatisticsTask(payload);
  };

  const validateDateField = (value: any) => validationDate(value, errorsMessages);

  return (
    <Box>
      <Typography variant="h6" className={styles.title}>
        {t("admin.full_report.return_player.title")}
      </Typography>

      <Typography variant="body2" className={styles.subtitle} color="textSecondary">
        {t("admin.full_report.return_player.subtitle")}
      </Typography>

      <Box className={styles.infoBanner}>
        <InfoOutlinedIcon className={styles.infoBannerIcon}/>
        <Typography variant="body2" className={styles.infoBannerText}>
          {t("admin.full_report.return_player.recommend_restore_cpa_hybrid")}
        </Typography>
      </Box>

      <Formik
        validateOnMount
        validateOnChange
        validateOnBlur
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, isSubmitting, isValid }) => {
          const disableSubmit =
            restoreDatesIsFetching ||
            !restoreDatesData?.ftdDate ||
            !isValid;
          return (
            <Form>
              <Typography variant="body2" className={styles.sectionLabel} color="textSecondary">
                {t("admin.full_report.return_player.return_ftd_from_date")}
              </Typography>

              <MuiSingleDatePicker
                inputFormat="yyyy-MM-dd"
                id="ftd-date"
                name="ftdDate"
                label={t("admin.reports.filter.date")}
                value={values.ftdDate ?? ""}
                maxDate={restoreDatesData?.dateTo ?? undefined}
                disabled
              />
              <div className={styles.divider} />

              <FormControlLabel className={styles.checkboxRow} 
                control={
                  <Checkbox
                    name="returnStats"
                    checked={values.returnStats}
                    onChange={handleChange}
                  />
                }
                label={
                  <Box className={styles.checkboxLabel}>
                    <Typography variant="body1" color="textPrimary">
                      {t("admin.full_report.return_player.return_other_stats")}
                    </Typography>
                    <Tooltip title={t("admin.full_report.return_player.return_stats_tooltip")} placement="top-start">
                      <span
                        className={styles.tooltipIconWrap}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </span>
                    </Tooltip>
                  </Box>
                }
              />

              <Box className={styles.dateRow}>
                <MuiSingleDatePicker
                  inputFormat="yyyy-MM-dd"
                  id="date-from"
                  name="dateFrom"
                  label={t("admin.reports.filter.fromDate")}
                  value={values.dateFrom}
                  disabled={!values.returnStats || restoreDatesIsFetching || !restoreDatesData}
                  minDate={restoreDatesData?.dateFrom ?? undefined}
                  maxDate={values.dateTo ?? restoreDatesData?.dateTo ?? undefined}
                  {...(values.returnStats ? { validate: validateDateField } : {})}
                />
                <MuiSingleDatePicker
                  inputFormat="yyyy-MM-dd"
                  id="date-to"
                  name="dateTo"
                  label={t("admin.reports.filter.toDate")}
                  value={values.dateTo ?? ""}
                  disabled={!values.returnStats || restoreDatesIsFetching || !restoreDatesData}
                  minDate={values.dateFrom ?? restoreDatesData?.dateFrom ?? undefined}
                  maxDate={restoreDatesData?.dateTo ?? undefined}
                  {...(values.returnStats ? { validate: validateDateField } : {})}
                />
              </Box>

              <Box className={styles.actions}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      gradient
                      fullWidth
                      disableElevation
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting || disableSubmit}
                    >
                      {t("common.buttons.proceed")}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      disableElevation
                      type="button"
                      variant="outlined"
                      color="secondary"
                      onClick={() => setToggleModal(false)}
                    >
                      {t("common.buttons.cancel")}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              <Typography variant="body2" className={styles.footerNote} color="textSecondary">
                {t("admin.full_report.return_player.background_restore_notice")}
              </Typography>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default ReturnPlayerModal;
